import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import type { ReactNode } from "react";
import { AuthProvider, useAuth } from "./auth/AuthContext.js";
import { ToastProvider } from "./components/ToastContext.js";
import { AppShell } from "./components/AppShell.js";
import { HomePage } from "./routes/HomePage.js";
import { LoginPage } from "./routes/LoginPage.js";
import { SetupPage } from "./routes/SetupPage.js";
import { PairingPage } from "./routes/PairingPage.js";
import { WritePage } from "./routes/WritePage.js";
import { DetailPage } from "./routes/DetailPage.js";
import { AnniversariesPage } from "./routes/AnniversariesPage.js";
import { SettingsPage } from "./routes/SettingsPage.js";
import { PasswordPage } from "./routes/PasswordPage.js";
import { SessionsPage } from "./routes/SessionsPage.js";
import { ExportPage } from "./routes/ExportPage.js";
import { NotFoundPage } from "./routes/NotFoundPage.js";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 401 不该重试三次才告诉用户没登录；其余错误重试一次就够。
      retry: 1,
      refetchOnWindowFocus: true,
      staleTime: 10_000,
    },
  },
});

/**
 * 路由守卫。
 *
 * 只做两件事：没初始化就送去 setup；没登录就送去 login。**不做权限判断** ——
 * 「能看到什么」由后端决定，前端最多决定「显示什么」（见 lib/entries.ts）。
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const { user, initialized, loading } = useAuth();
  if (loading) {
    return (
      <div className="auth">
        <div className="spinner" />
      </div>
    );
  }
  if (initialized === false) return <Navigate to="/setup" replace />;
  if (user === null) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/setup" element={<SetupPage />} />
              <Route
                element={
                  <RequireAuth>
                    <AppShell />
                  </RequireAuth>
                }
              >
                <Route index element={<HomePage />} />
                <Route path="entries/new" element={<WritePage />} />
                <Route path="entries/:id" element={<DetailPage />} />
                <Route path="entries/:id/edit" element={<WritePage />} />
                <Route path="anniversaries" element={<AnniversariesPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="settings/password" element={<PasswordPage />} />
                <Route path="settings/sessions" element={<SessionsPage />} />
                <Route path="export" element={<ExportPage />} />
                <Route path="pairing" element={<PairingPage />} />
              </Route>
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
